import moment from 'moment';
import { MouseEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { useLocalize } from '@treams/treams-hooks';
import * as graphql from '@treams/treams-network';
import {
	ArchiveIcon,
	ArrowRightIcon,
	Avatar,
	Button,
	Checkbox,
	CheckmarkIcon,
	ContextMenu,
	Ellipsis,
	IconButton,
	MoreAlternateIcon,
	Paragraph,
	Spinner,
	Table,
	tokens,
} from '@treams/treams-ui';

import { useJobTitleName } from '../job/useJobTitleName';

const CHECKBOX_WIDTH = 16;
const TEMPLATE_WIDTH = 140;
const DEADLINE_WIDTH = 80;
const STATUS_WIDTH = 96;
const ACTIONS_WIDTH = 140;

const Deadline = styled.span<{ $overdue?: boolean }>`
	color: ${(props) => (props.$overdue ? tokens.error.text : 'inherit')};
`;

function stopRowNavigation(event: MouseEvent) {
	event.preventDefault();
	event.stopPropagation();
}

function readableConversationNextStep(
	evaluationSubmission: graphql.EvaluationSubmission,
): 'prepare' | 'confirm' | 'complete' | 'archived' | 'none' {
	if (evaluationSubmission.manager.isCompleted) {
		return 'none';
	}

	if (evaluationSubmission.isArchived) {
		return 'archived';
	}

	if (evaluationSubmission.manager.requiresSignature) {
		if (evaluationSubmission.manager.status === graphql.EvaluationSubmissionStatus.Signed) {
			return 'complete';
		}
		if (evaluationSubmission.manager.hasShared) {
			return 'confirm';
		}
	}

	if (evaluationSubmission.manager.hasShared) {
		return 'complete';
	}
	return 'prepare';
}

function ConversationPerson(props: { user: graphql.User }) {
	const jobTitleName = useJobTitleName();
	const jobTitle = props.user.currentJobPosition
		? jobTitleName(props.user.currentJobPosition.jobTitle)
		: undefined;

	return (
		<>
			<Avatar user={props.user} size={32} />
			<Box minWidth={0} flex={1}>
				<Ellipsis>
					{props.user.firstName} {props.user.lastName}
				</Ellipsis>
				{jobTitle && (
					<Paragraph small>
						<Ellipsis>{jobTitle}</Ellipsis>
					</Paragraph>
				)}
			</Box>
		</>
	);
}

export function ConversationsTableHeader() {
	const { t } = useTranslation();

	return (
		<Table.Header>
			<Table.Cell width={CHECKBOX_WIDTH} />
			<Table.Cell grow>
				<Ellipsis>{t('managerInbox.table.header.name')}</Ellipsis>
			</Table.Cell>
			<Table.Cell width={TEMPLATE_WIDTH}>
				<Ellipsis>{t('manager.conversations.forecast.table.heading.template')}</Ellipsis>
			</Table.Cell>
			<Table.Cell width={DEADLINE_WIDTH}>
				<Ellipsis>{t('manager.conversations.forecast.table.heading.deadline')}</Ellipsis>
			</Table.Cell>
			<Table.Cell width={STATUS_WIDTH}>
				<Ellipsis>{t('manager.conversations.forecast.table.heading.status')}</Ellipsis>
			</Table.Cell>
			<Table.Cell width={ACTIONS_WIDTH} />
		</Table.Header>
	);
}

export function ConversationsTableEmptyRow(props: { message: string }) {
	return (
		<Table.Row tall>
			<Table.Cell grow>{props.message}</Table.Cell>
		</Table.Row>
	);
}

export function ConversationsTableRow(props: {
	evaluationSubmission: graphql.EvaluationSubmission;
	onArchive: (evaluationSubmission: graphql.EvaluationSubmission) => Promise<void>;
	onComplete: (evaluationSubmission: graphql.EvaluationSubmission) => Promise<void>;
	onUncomplete: (evaluationSubmission: graphql.EvaluationSubmission) => Promise<void>;
}) {
	const { localize } = useLocalize();
	const { t } = useTranslation();
	const [loading, setLoading] = useState(false);

	const completed = props.evaluationSubmission.manager.isCompleted;
	const nextStep = readableConversationNextStep(props.evaluationSubmission);
	const isStart = nextStep === 'prepare';
	const overdue =
		moment(props.evaluationSubmission.deadline).isBefore(moment().startOf('day')) && !completed;

	return (
		<Table.Row
			tall
			link={`/users/${props.evaluationSubmission.employee.user.id}/conversations/${props.evaluationSubmission.id}?return=/manager`}
		>
			<Table.Cell width={CHECKBOX_WIDTH}>
				<Box onClick={stopRowNavigation}>
					{loading ? (
						<Flex width={16} height={16} alignItems="center" justifyContent="center">
							<Spinner />
						</Flex>
					) : (
						<Checkbox
							mr={0}
							checked={completed}
							onClick={async () => {
								setLoading(true);
								if (completed) {
									await props.onUncomplete(props.evaluationSubmission);
								} else {
									await props.onComplete(props.evaluationSubmission);
								}
								setLoading(false);
							}}
						/>
					)}
				</Box>
			</Table.Cell>
			<Table.Cell grow>
				<ConversationPerson user={props.evaluationSubmission.employee.user} />
			</Table.Cell>
			<Table.Cell width={TEMPLATE_WIDTH}>
				<Ellipsis>{localize(props.evaluationSubmission.evaluationForm.title).text}</Ellipsis>
			</Table.Cell>
			<Table.Cell width={DEADLINE_WIDTH}>
				<Deadline $overdue={overdue}>
					{moment(props.evaluationSubmission.deadline).format('L')}
				</Deadline>
			</Table.Cell>
			<Table.Cell width={STATUS_WIDTH}>
				<Ellipsis>{t(`conversation.table.row.progress.${nextStep}`)}</Ellipsis>
			</Table.Cell>
			<Table.Cell width={ACTIONS_WIDTH} align="right">
				<ContextMenu.Target
					menu={
						<ContextMenu>
							{!props.evaluationSubmission.manager.isCompleted && (
								<ContextMenu.Item
									title={t('menu.actions.completeConversation')}
									icon={<CheckmarkIcon />}
									onClick={async () => {
										setLoading(true);
										await props.onComplete(props.evaluationSubmission);
										setLoading(false);
									}}
								/>
							)}
							<ContextMenu.Item
								title={t('menu.actions.archiveConversation')}
								icon={<ArchiveIcon />}
								onClick={async () => {
									setLoading(true);
									await props.onArchive(props.evaluationSubmission);
									setLoading(false);
								}}
							/>
						</ContextMenu>
					}
				>
					<IconButton type="secondary" icon={<MoreAlternateIcon />} loading={loading} />
				</ContextMenu.Target>
				<Button
					type={isStart ? 'primary' : 'secondary'}
					title={
						isStart
							? t('manager.conversations.forecast.start.button')
							: t('inbox.tasks.button.view')
					}
					rightIcon={<ArrowRightIcon />}
				/>
			</Table.Cell>
		</Table.Row>
	);
}

export function ConversationsTableMomentRow(props: {
	momentInstance: graphql.MomentInstance;
	onSkip: (momentInstance: graphql.MomentInstance) => Promise<void>;
}) {
	const { localize } = useLocalize();
	const { t } = useTranslation();
	const overdue = moment(props.momentInstance.endDate).isBefore(moment().startOf('day'));
	const template = props.momentInstance.momentTemplate.evaluation?.evaluationForm
		? localize(props.momentInstance.momentTemplate.evaluation.evaluationForm.title).text
		: undefined;

	return (
		<Table.Row
			tall
			link={`/users/${props.momentInstance.user.id}/conversations/create-from-moment/${props.momentInstance.id}`}
		>
			<Table.Cell width={CHECKBOX_WIDTH} />
			<Table.Cell grow>
				<ConversationPerson user={props.momentInstance.user} />
			</Table.Cell>
			<Table.Cell width={TEMPLATE_WIDTH}>{template && <Ellipsis>{template}</Ellipsis>}</Table.Cell>
			<Table.Cell width={DEADLINE_WIDTH}>
				<Deadline $overdue={overdue}>{moment(props.momentInstance.endDate).format('L')}</Deadline>
			</Table.Cell>
			<Table.Cell width={STATUS_WIDTH}>
				<Ellipsis>{t('conversation.table.row.progress.prepare')}</Ellipsis>
			</Table.Cell>
			<Table.Cell width={ACTIONS_WIDTH} align="right">
				<Button
					type="primary"
					title={t('manager.conversations.forecast.create.button')}
					rightIcon={<ArrowRightIcon />}
				/>
			</Table.Cell>
		</Table.Row>
	);
}
